/**
 * Change what a requirement type says, and give it more attributes.
 *
 * TEMPLATE AND NEW ATTRIBUTES ONLY. Rename, re-base and remove-an-attribute are
 * not here, and their absence is the design rather than the backlog: a rename
 * breaks every requirement typed by it, a re-base can silently change their
 * level, and removing an attribute orphans values they already state. The
 * server refuses all three; offering them here would be a form that promises
 * what the API declines.
 *
 * The template box is seeded from the type's OWN template, never the inherited
 * one. Seeding it from what it renders would mean opening this dialog and
 * saving - with no edit at all - copied a base's template onto the subtype and
 * quietly stopped it following the base.
 */

import { useEffect, useState } from 'react'
import Alert from '@mui/material/Alert'
import Box from '@mui/material/Box'
import Button from '@mui/material/Button'
import Dialog from '@mui/material/Dialog'
import DialogActions from '@mui/material/DialogActions'
import DialogContent from '@mui/material/DialogContent'
import DialogTitle from '@mui/material/DialogTitle'
import Stack from '@mui/material/Stack'
import TextField from '@mui/material/TextField'
import Typography from '@mui/material/Typography'
import NewAttributesTable from '../parts/NewAttributesTable'
import { attributePayload, emptyAttributeDraft } from '../parts/part-wizard-model'
import type { AttributeDraft } from '../parts/part-wizard-model'
import { ApiError, api } from '../../api'
import { useToasts } from '../../state/Toasts'
import { MONOSPACE } from '../../theme'
import type { AttributeTypeChoice, RequirementTypeRow } from '../../schema'

export default function EditRequirementTypeDialog({
  type,
  onClose,
  onSaved,
}: {
  type: RequirementTypeRow
  onClose: () => void
  onSaved: () => void
}) {
  const toasts = useToasts()

  const [template, setTemplate] = useState(type.own_template)
  const [attributes, setAttributes] = useState<AttributeDraft[]>([])
  const [busy, setBusy] = useState(false)
  const [failure, setFailure] = useState('')

  const [attributeTypes, setAttributeTypes] = useState<AttributeTypeChoice[]>([])
  useEffect(() => {
    let live = true
    api
      .attributeTypes()
      .then((answer) => live && setAttributeTypes(answer))
      .catch(() => live && setAttributeTypes([]))
    return () => {
      live = false
    }
  }, [])

  const declaring = attributePayload(attributes)
  const changed = template !== type.own_template || declaring.length > 0

  const save = async () => {
    setBusy(true)
    setFailure('')
    try {
      await api.updateRequirementType(type.qualified_name, {
        // Only when it actually changed: an unchanged template still sent would
        // write the def and rewrite the file for nothing.
        ...(template !== type.own_template ? { template } : {}),
        ...(declaring.length ? { attributes: declaring } : {}),
      })
      toasts.succeeded(`${type.name} updated`)
      onSaved()
    } catch (error) {
      setFailure((error as ApiError).message)
    } finally {
      setBusy(false)
    }
  }

  return (
    <Dialog open onClose={busy ? undefined : onClose} maxWidth="md" fullWidth>
      <DialogTitle sx={{ fontFamily: MONOSPACE }}>{type.name}</DialogTitle>
      <DialogContent>
        <Stack spacing={2} sx={{ pt: 1 }}>
          {failure && <Alert severity="error">{failure}</Alert>}

          {type.used_by.length > 0 && (
            <Alert severity="info">
              {type.used_by.length} requirement(s) render from this template. Saving
              re-writes every one of their statements.
            </Alert>
          )}

          <TextField
            label="Statement template"
            value={template}
            onChange={(event) => setTemplate(event.target.value)}
            multiline
            minRows={2}
            size="small"
            disabled={busy}
            helperText={
              !type.own_template && type.template
                ? `Empty means it follows its base: "${type.template}"`
                : "Name a feature in braces, e.g. 'The {allocated_to} shall have a mass {mass}.'"
            }
          />

          {type.attributes.length > 0 && (
            <Typography variant="caption" color="text.secondary">
              Already declares {type.attributes.map((entry) => entry.name).join(', ')}.
              Removing one is not offered here: requirements already state values for
              it.
            </Typography>
          )}

          <Box>
            <NewAttributesTable
              rows={attributes}
              choices={attributeTypes}
              onChange={setAttributes}
              disabled={busy}
              showRollsUp={false}
              showLinks
            />
            {attributes.length === 0 && (
              <Button size="small" onClick={() => setAttributes([emptyAttributeDraft('attr-0')])}>
                Declare another attribute
              </Button>
            )}
          </Box>
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={busy}>
          Cancel
        </Button>
        <Button variant="contained" onClick={() => void save()} disabled={!changed || busy}>
          {busy ? 'Saving…' : 'Save'}
        </Button>
      </DialogActions>
    </Dialog>
  )
}
